import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import api from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

function isTokenValid(t) {
  if (!t) return false;
  try {
    const payload = JSON.parse(atob(t.split('.')[1]));
    return payload.exp * 1000 > Date.now();
  } catch {
    return false;
  }
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => {
    const stored = localStorage.getItem('token');
    if (stored && !isTokenValid(stored)) {
      localStorage.removeItem('token');
      return null;
    }
    return stored;
  });
  const [loading, setLoading] = useState(true);
  // isOwner/permissions aren't JWT claims — they're fetched from
  // /api/admin/staff/me so a permission change or deactivation takes
  // effect without needing a refresh-token/blacklist scheme. Server-side
  // enforcement (StaffPermissionVoter) is always the source of truth; this
  // is just for sidebar/route gating in the UI.
  const [isOwner, setIsOwner] = useState(true);
  const [permissions, setPermissions] = useState([]);

  const fetchMe = useCallback(async (authToken) => {
    try {
      const res = await api.get('/api/admin/staff/me', {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setIsOwner(res.data.isOwner);
      setPermissions(res.data.permissions || []);
      setUser({ email: res.data.email, name: res.data.name });
    } catch {
      // Leave defaults (isOwner: true) so a pre-Phase-6 driver/admin token
      // that predates this endpoint doesn't get incorrectly locked out —
      // real enforcement happens server-side regardless.
    }
  }, []);

  useEffect(() => {
    if (token) {
      fetchMe(token).finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const login = async (email, password) => {
    try {
      const response = await api.post('/api/login', { email, password });
      const { token: newToken } = response.data;

      setToken(newToken);
      localStorage.setItem('token', newToken);
      await fetchMe(newToken);

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Login failed'
      };
    }
  };

  // For flows that receive a JWT outside the normal email/password login
  // (e.g. staff invite setup, which auto-logs-in on success) — updates
  // React state, not just localStorage, so isOwner/permissions refetch via
  // the effect above instead of staying stuck on whatever was in memory
  // from a previous session in the same tab.
  const setAuthToken = (newToken) => {
    setToken(newToken);
    localStorage.setItem('token', newToken);
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    setIsOwner(true);
    setPermissions([]);
    localStorage.removeItem('token');
  };

  const hasPermission = (area) => isOwner || permissions.includes(area);

  const value = {
    user,
    token,
    login,
    logout,
    isAuthenticated: !!token,
    loading,
    isOwner,
    permissions,
    hasPermission,
    setAuthToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
